import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  type ParentProps,
} from 'solid-js';
import { A, useLocation } from '@solidjs/router';
import { Button, Icon, IconButton, StatusPill, UnstyledButton, SelectControl } from '@academy/ui';
import type { ProductRole } from '@academy/domain';
import { useSession } from '../context/session';
import { routeCapabilities } from '../routes/route-access';
import {
  primarySidebarRouteIds,
  workspaceRouteMetadataById,
} from '../routes/workspace-route-metadata';
import { getSupabase } from '../services/supabase';
import { clearQueryCacheForAccount } from '../services/query-cache';
import { purgeAllQuerySnapshots } from '../services/query-snapshot-storage';
import {
  beginWorkspaceRoutePrefetch,
  clearWorkspacePrefetchRecordsForAccount,
  scheduleIdleWorkspacePrefetch,
  type WorkspacePrefetchHandle,
} from '../services/workspace-prefetch';
import {
  navigationPerformanceEvent,
  sanitizeNavigationRoute,
  startNavigationTelemetry,
  type NavigationPerformanceDetail,
} from '../services/navigation-telemetry';
import styles from './app-shell.module.css';
import { buildRoleNavigationGroups } from './role-workspace-navigation';

const navigation = primarySidebarRouteIds.map(workspaceRouteMetadataById);
const governanceNavigation = workspaceRouteMetadataById('governance');

const displayRole = (role: ProductRole | null) =>
  role
    ? role
        .replaceAll('_', ' ')
        .toLocaleLowerCase()
        .replace(/(^|\s)\S/gu, (letter) => letter.toLocaleUpperCase())
    : 'Member';

const workspaceLabels: readonly { prefix: string; label: string }[] = [
  { prefix: '/studio/presentations/versions/', label: 'Presentation editor' },
  { prefix: '/learning/progress/', label: 'Learning progress' },
  { prefix: '/admin/access/', label: 'Access administration' },
  { prefix: '/operations/', label: 'Operations' },
  { prefix: '/academy/', label: 'Academy governance' },
  { prefix: '/recordings/', label: 'Recording review' },
  { prefix: '/meetings/', label: 'Live meeting' },
  { prefix: '/deliveries', label: 'Delivery' },
  { prefix: '/learn/', label: 'Self-paced learning' },
  { prefix: '/assignments/', label: 'Assignment' },
  { prefix: '/sessions/', label: 'Class session' },
  { prefix: '/people/', label: 'People' },
  { prefix: '/groups', label: 'Groups' },
  { prefix: '/curriculum/', label: 'Curriculum' },
  { prefix: '/schedule', label: 'Schedule' },
  { prefix: '/studio/', label: 'Presentation studio' },
  { prefix: '/learning', label: 'Learning' },
  { prefix: '/assessment', label: 'Assessment' },
  { prefix: '/messages', label: 'Messages' },
  { prefix: '/privacy', label: 'Privacy and safety' },
  { prefix: '/account', label: 'Account' },
  { prefix: '/', label: 'Overview' },
];

export const AppShell = (props: ParentProps) => {
  const session = useSession();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = createSignal(false);
  const [compactNavigation, setCompactNavigation] = createSignal(false);
  const [online, setOnline] = createSignal(navigator.onLine);
  let menuButton: HTMLButtonElement | undefined;
  let navigationPanel: HTMLElement | undefined;
  let announcedNavigationId: string | null = null;
  let previousActiveRole: ProductRole | null | undefined;
  let lastMeaningfulRoute = sanitizeNavigationRoute(`${location.pathname}${location.search}`);
  const navigationIntents = new Map<string, WorkspacePrefetchHandle>();
  const committedNavigationIntents = new Set<string>();
  const workspaceLabel = createMemo(
    () =>
      workspaceLabels.find(({ prefix }) => location.pathname.startsWith(prefix))?.label ??
      'Academy',
  );
  const workspaceAnnouncementLabel = createMemo(() =>
    location.pathname === '/'
      ? `${workspaceLabel()} for ${displayRole(session.activeRole())}`
      : workspaceLabel(),
  );
  const [announcedWorkspaceLabel, setAnnouncedWorkspaceLabel] = createSignal(
    workspaceAnnouncementLabel(),
  );
  const [meaningfulNavigation, setMeaningfulNavigation] =
    createSignal<NavigationPerformanceDetail | null>(null);
  const allowedNavigation = createMemo(() => {
    const activeRole = session.activeRole();
    return navigation.filter(
      (item) =>
        activeRole &&
        (routeCapabilities[item.capability] as readonly ProductRole[]).includes(activeRole),
    );
  });
  const groupedNavigation = createMemo(() =>
    buildRoleNavigationGroups(session.activeRole(), allowedNavigation()),
  );
  const mayViewAcademyGovernance = createMemo(() => {
    const activeRole = session.activeRole();
    return activeRole
      ? routeCapabilities.academyAdministration.some((role) => role === activeRole)
      : false;
  });

  onMount(() => {
    const connected = () => setOnline(true);
    const disconnected = () => setOnline(false);
    const compactQuery = window.matchMedia?.('(max-width: 920px)');
    const syncCompactNavigation = () => setCompactNavigation(compactQuery?.matches ?? false);
    window.addEventListener('online', connected);
    window.addEventListener('offline', disconnected);
    const announceMeaningfulRoute = (event: Event) => {
      const detail = (event as CustomEvent<NavigationPerformanceDetail>).detail;
      if (detail.phase === 'meaningful-content') setMeaningfulNavigation(detail);
    };
    window.addEventListener(navigationPerformanceEvent, announceMeaningfulRoute);
    syncCompactNavigation();
    compactQuery?.addEventListener('change', syncCompactNavigation);
    const cancelIdlePrefetch = scheduleIdleWorkspacePrefetch({
      allowedHrefs: allowedNavigation().map((item) => item.href),
      authority: () => ({
        accountId: session.accountId,
        activeRole: session.activeRole(),
        roles: session.roles,
        sessionSubjectHash: session.sessionSubjectHash,
        sessionGenerationHash: session.sessionGenerationHash,
        onAuthoritativeDenial: () => {
          void session.refreshCapabilities('invalidation').catch(() => undefined);
        },
      }),
    });
    onCleanup(() => {
      window.removeEventListener('online', connected);
      window.removeEventListener('offline', disconnected);
      window.removeEventListener(navigationPerformanceEvent, announceMeaningfulRoute);
      compactQuery?.removeEventListener('change', syncCompactNavigation);
      cancelIdlePrefetch();
      for (const intent of navigationIntents.values()) intent.cancel();
      navigationIntents.clear();
      committedNavigationIntents.clear();
    });
  });

  createEffect(() => {
    const path = location.pathname;
    const search = location.search;
    startNavigationTelemetry(`${path}${search}`);
  });

  createEffect(() => {
    const detail = meaningfulNavigation();
    const currentLocation = `${location.pathname}${location.search}`;
    if (
      detail?.route !== sanitizeNavigationRoute(currentLocation) ||
      detail.navigationId === announcedNavigationId
    ) {
      return;
    }
    announcedNavigationId = detail.navigationId;
    setAnnouncedWorkspaceLabel(workspaceAnnouncementLabel());
    if (detail.route !== lastMeaningfulRoute) {
      lastMeaningfulRoute = detail.route;
      queueMicrotask(() =>
        document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true }),
      );
    }
  });

  createEffect(() => {
    const role = session.activeRole();
    const label = workspaceLabel();
    const roleLabel = displayRole(role);
    document.title =
      location.pathname === '/' && (role === 'STUDENT' || role === 'TEACHER')
        ? `${roleLabel} Today · Rubiano RealSpeak Academy`
        : `${label} · ${roleLabel} · Rubiano RealSpeak Academy`;
    if (previousActiveRole !== undefined && role !== previousActiveRole) {
      setAnnouncedWorkspaceLabel(workspaceAnnouncementLabel());
      queueMicrotask(() =>
        document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true }),
      );
    }
    previousActiveRole = role;
  });

  const setNavigationOpen = (open: boolean, restoreFocus = false) => {
    setMobileOpen(open);
    queueMicrotask(() => {
      if (open) {
        navigationPanel?.querySelector<HTMLElement>('a[href]')?.focus();
      } else if (restoreFocus) {
        menuButton?.focus();
      }
    });
  };

  const signOut = async () => {
    clearWorkspacePrefetchRecordsForAccount(session.accountId);
    clearQueryCacheForAccount(session.accountId);
    purgeAllQuerySnapshots();
    await getSupabase().auth.signOut({ scope: 'local' });
    window.location.assign('/');
  };

  const changeRole = (role: ProductRole) => {
    session.setActiveRole(role);
    if (compactNavigation()) setNavigationOpen(false);
  };

  const startNavigationIntent = (
    href: string,
    trigger: 'pointer' | 'keyboard' | 'touch' | 'navigation',
  ) => {
    if (navigationIntents.has(href)) return;
    const handle = beginWorkspaceRoutePrefetch({
      href,
      trigger,
      authority: {
        accountId: session.accountId,
        activeRole: session.activeRole(),
        roles: session.roles,
        sessionSubjectHash: session.sessionSubjectHash,
        sessionGenerationHash: session.sessionGenerationHash,
        onAuthoritativeDenial: () => {
          void session.refreshCapabilities('invalidation').catch(() => undefined);
        },
      },
    });
    navigationIntents.set(href, handle);
    void handle.promise.finally(() => {
      if (navigationIntents.get(href) === handle) navigationIntents.delete(href);
      committedNavigationIntents.delete(href);
    });
  };

  const abandonNavigationIntent = (href: string) => {
    if (committedNavigationIntents.has(href)) return;
    const handle = navigationIntents.get(href);
    handle?.cancel();
    navigationIntents.delete(href);
  };

  return (
    <div class={styles.shell}>
      <header class={styles.topbar}>
        <IconButton
          ref={menuButton}
          class={styles.mobileMenu}
          icon="menu"
          label={mobileOpen() ? 'Close navigation' : 'Open navigation'}
          variant="ghost"
          density="compact"
          aria-expanded={mobileOpen()}
          aria-controls="primary-navigation-panel"
          onClick={() => setNavigationOpen(!mobileOpen(), mobileOpen())}
        />
        <A href="/" class={styles.wordmark} aria-label="Rubiano RealSpeak Academy overview">
          <span class={styles.logo} aria-hidden="true">
            V
          </span>
          <span>Rubiano</span>
        </A>
        <div class={styles.topbarContext}>
          <span class={styles.academyName}>RealSpeak Academy</span>
          <StatusPill tone={online() ? 'success' : 'warning'}>
            {online() ? 'Connected' : 'Offline'}
          </StatusPill>
        </div>
        <div class={styles.account}>
          <A href="/account" class={styles.avatar} aria-label="Open your account">
            {(session.displayName ?? session.email ?? 'A').slice(0, 1).toUpperCase()}
          </A>
          <span class={styles.accountCopy}>
            <strong>{session.displayName ?? 'Academy member'}</strong>
            {session.roles.length > 1 ? (
              <Show when={!compactNavigation()}>
                <SelectControl
                  aria-label="Active workspace role"
                  value={session.activeRole() ?? undefined}
                  onChange={(event) => changeRole(event.currentTarget.value as ProductRole)}
                >
                  <For each={session.roles}>
                    {(role) => <option value={role}>{role.replaceAll('_', ' ')}</option>}
                  </For>
                </SelectControl>
              </Show>
            ) : (
              <small>{session.activeRole()?.replaceAll('_', ' ') ?? 'Member'}</small>
            )}
          </span>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>

      <aside
        ref={navigationPanel}
        id="primary-navigation-panel"
        class={`${styles.sidebar} ${mobileOpen() ? styles.mobileOpen : ''}`}
        aria-hidden={compactNavigation() && !mobileOpen() ? 'true' : undefined}
        inert={compactNavigation() && !mobileOpen()}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && mobileOpen()) {
            event.preventDefault();
            setNavigationOpen(false, true);
          }
        }}
      >
        <Show when={compactNavigation() && session.roles.length > 1}>
          <label class={styles.mobileRoleSwitcher}>
            Workspace role
            <SelectControl
              aria-label="Active workspace role"
              value={session.activeRole() ?? undefined}
              onChange={(event) => changeRole(event.currentTarget.value as ProductRole)}
            >
              <For each={session.roles}>
                {(role) => <option value={role}>{role.replaceAll('_', ' ')}</option>}
              </For>
            </SelectControl>
          </label>
        </Show>
        <nav aria-label="Primary navigation">
          <For each={groupedNavigation()}>
            {(group, index) => {
              const groupId = () => `workspace-navigation-group-${index()}`;
              return (
                <section class={styles.navGroup} aria-labelledby={groupId()}>
                  <p id={groupId()} class={styles.navGroupLabel}>
                    {group.label}
                  </p>
                  <div class={styles.navGroupItems}>
                    <For each={group.items}>
                      {(item) => (
                        <A
                          href={item.href}
                          end={item.href === '/'}
                          class={styles.navItem}
                          activeClass={styles.active}
                          onPointerEnter={() => startNavigationIntent(item.href, 'pointer')}
                          onPointerLeave={() => abandonNavigationIntent(item.href)}
                          onPointerDown={(event) => {
                            if (event.pointerType !== 'mouse')
                              startNavigationIntent(item.href, 'touch');
                          }}
                          onFocus={() => startNavigationIntent(item.href, 'keyboard')}
                          onBlur={() => abandonNavigationIntent(item.href)}
                          onClick={() => {
                            if (!navigationIntents.has(item.href)) {
                              startNavigationIntent(item.href, 'navigation');
                            }
                            if (navigationIntents.has(item.href)) {
                              committedNavigationIntents.add(item.href);
                            }
                            setNavigationOpen(false);
                          }}
                        >
                          <Icon name={item.icon} size={20} />
                          {item.label}
                        </A>
                      )}
                    </For>
                  </div>
                </section>
              );
            }}
          </For>
        </nav>
        <div class={styles.sidebarFooter}>
          <Show when={mayViewAcademyGovernance()}>
            <A
              href={governanceNavigation.href}
              class={styles.navItem}
              activeClass={styles.active}
              aria-current={location.pathname.startsWith('/academy/') ? 'page' : undefined}
            >
              <Icon name={governanceNavigation.icon} size={20} />
              {governanceNavigation.label}
            </A>
          </Show>
          <p>
            Audio-first teaching
            <br />
            <small>No participant video</small>
          </p>
        </div>
      </aside>

      <main id="main-content" class={styles.main} tabindex="-1">
        <p
          id="route-announcer"
          class={styles.visuallyHidden}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcedWorkspaceLabel()} workspace loaded.
        </p>
        {!online() && (
          <div class={styles.offline} role="status">
            You are offline. Unsaved work will remain visible and reconnect safely.
          </div>
        )}
        {props.children}
      </main>
      {mobileOpen() && (
        <UnstyledButton
          class={styles.scrim}
          aria-label="Close navigation"
          onClick={() => setNavigationOpen(false, true)}
        />
      )}
    </div>
  );
};
